import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Notification {
  id: string;
  type: 'buddy_read_invite' | 'buddy_read_progress' | 'level_up' | 'message' | 'achievement' | 'follow' | 'tag_book';
  title: string;
  description: string;
  icon?: string;
  actionUrl?: string;
  read: boolean;
  createdAt: string;
  metadata?: any;
}

interface NotificationPrefs {
  buddy_read_invites: boolean;
  buddy_read_progress: boolean;
  messages: boolean;
  friend_level_ups: boolean;
  friend_achievements: boolean;
  follow_requests: boolean;
  system_announcements: boolean;
}

export function useNotifications() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<NotificationPrefs | null>(null);

  // Load preferences
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) return;

      const { data } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data) {
        setPreferences({
          buddy_read_invites: data.buddy_read_invites,
          buddy_read_progress: data.buddy_read_progress,
          messages: data.messages,
          friend_level_ups: data.friend_level_ups,
          friend_achievements: data.friend_achievements,
          follow_requests: data.follow_requests,
          system_announcements: data.system_announcements,
        });
      }
    };

    loadPreferences();
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const allNotifications: Notification[] = [];

      // Fetch buddy read invitations
      const { data: invitations } = await supabase
        .from('buddy_read_members')
        .select(`
          id,
          created_at,
          buddy_read:buddy_reads(
            id,
            title,
            book:books(title),
            creator:profiles!buddy_reads_created_by_fkey(username, name)
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'invited')
        .order('created_at', { ascending: false })
        .limit(10);

      invitations?.forEach((inv: any) => {
        const buddyRead = Array.isArray(inv.buddy_read) ? inv.buddy_read[0] : inv.buddy_read;
        const book = Array.isArray(buddyRead?.book) ? buddyRead.book[0] : buddyRead?.book;
        const creator = Array.isArray(buddyRead?.creator) ? buddyRead.creator[0] : buddyRead?.creator;

        allNotifications.push({
          id: `buddy_invite_${inv.id}`,
          type: 'buddy_read_invite',
          title: 'New Buddy Read Invitation',
          description: `${creator?.name || creator?.username} invited you to read "${book?.title}"`,
          actionUrl: '/buddy-reads',
          read: false,
          createdAt: inv.created_at,
          metadata: { buddyReadId: buddyRead?.id }
        });
      });

      // Fetch recent messages (unread count)
      const { data: chatsData } = await supabase
        .from('chat_members')
        .select(`
          chat_id,
          last_read_at,
          chat:chats(
            id,
            last_message_at,
            is_group,
            title
          )
        `)
        .eq('user_id', user.id);

      if (chatsData) {
        for (const chatMember of chatsData) {
          const chat = Array.isArray(chatMember.chat) ? chatMember.chat[0] : chatMember.chat;
          
          if (chat && chat.last_message_at) {
            const lastReadAt = chatMember.last_read_at || new Date(0).toISOString();
            if (new Date(chat.last_message_at) > new Date(lastReadAt)) {
              allNotifications.push({
                id: `message_${chat.id}`,
                type: 'message',
                title: 'New Message',
                description: chat.is_group ? `New message in ${chat.title || 'group chat'}` : 'You have a new message',
                actionUrl: `/chat/${chat.id}`,
                read: false,
                createdAt: chat.last_message_at
              });
            }
          }
        }
      }

      // Fetch follow requests (fetch profiles separately since FK doesn't exist)
      const { data: followRequests } = await supabase
        .from('follow_requests')
        .select('id, created_at, requester_id')
        .eq('requested_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(5);

      if (followRequests && followRequests.length > 0) {
        // Fetch requester profiles separately
        const requesterIds = followRequests.map(req => req.requester_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, name, image')
          .in('id', requesterIds);

        const profilesMap = new Map(profiles?.map(p => [p.id, p]) || []);

        followRequests.forEach((req: any) => {
          const requester = profilesMap.get(req.requester_id);
          allNotifications.push({
            id: `follow_${req.id}`,
            type: 'follow',
            title: 'New Follow Request',
            description: `${requester?.name || requester?.username || 'Someone'} wants to follow you`,
            actionUrl: '/follow-requests',
            read: false,
            createdAt: req.created_at,
            metadata: { requestId: req.id }
          });
        });
      }

      // Fetch tag notifications
      const { data: tagNotifications } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'tag_book')
        .order('created_at', { ascending: false })
        .limit(10);

      tagNotifications?.forEach((notif: any) => {
        allNotifications.push({
          id: `tag_${notif.id}`,
          type: 'tag_book',
          title: notif.title,
          description: notif.message,
          actionUrl: notif.link,
          read: notif.read,
          createdAt: notif.created_at,
          metadata: notif.metadata
        });
      });

      // Sort by date
      allNotifications.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setNotifications(allNotifications);
      setUnreadCount(allNotifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user || !preferences) return;

    const channel = supabase
      .channel('notifications')
      // Buddy read invitations
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'buddy_read_members',
          filter: `user_id=eq.${user.id}`
        },
        async (payload) => {
          if (payload.new.status === 'invited' && preferences.buddy_read_invites) {
            const { data: buddyReadData } = await supabase
              .from('buddy_reads')
              .select(`
                *,
                book:books(title),
                creator:profiles!buddy_reads_created_by_fkey(name, username)
              `)
              .eq('id', payload.new.buddy_read_id)
              .single();

            if (buddyReadData) {
              const creator = Array.isArray(buddyReadData.creator) 
                ? buddyReadData.creator[0] 
                : buddyReadData.creator;
              const book = Array.isArray(buddyReadData.book) 
                ? buddyReadData.book[0] 
                : buddyReadData.book;

              toast({
                title: "New Buddy Read Invitation!",
                description: `${creator?.name || creator?.username} invited you to read "${book?.title}"`,
              });
            }
            fetchNotifications();
          }
        }
      )
      // New messages
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chats'
        },
        (payload) => {
          // Check if user is a member of this chat
          supabase
            .from('chat_members')
            .select('chat_id')
            .eq('chat_id', payload.new.id)
            .eq('user_id', user.id)
            .single()
            .then(({ data }) => {
              if (data) {
                fetchNotifications();
              }
            });
        }
      )
      // Follow requests
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'follow_requests',
          filter: `requested_id=eq.${user.id}`
        },
        async (payload) => {
          if (!preferences.follow_requests) return;

          const { data: requesterData } = await supabase
            .from('profiles')
            .select('name, username')
            .eq('id', payload.new.requester_id)
            .single();

          if (requesterData) {
            toast({
              title: "New Follow Request",
              description: `${requesterData.name || requesterData.username} wants to follow you`,
            });
          }
          fetchNotifications();
        }
      )
      // Level ups from activity feed
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activities'
        },
        async (payload) => {
          if (payload.new.type === 'level_up' && preferences.friend_level_ups) {
            // Check if this is from a friend
            const { data: isFollowing } = await supabase
              .from('follows')
              .select('id')
              .eq('follower_id', user.id)
              .eq('following_id', payload.new.user_id)
              .eq('status', 'accepted')
              .maybeSingle();

            if (isFollowing) {
              const { data: profileData } = await supabase
                .from('profiles')
                .select('name, username, level')
                .eq('id', payload.new.user_id)
                .single();

              if (profileData) {
                toast({
                  title: "Friend Leveled Up! 🎉",
                  description: `${profileData.name || profileData.username} reached level ${profileData.level}`,
                });
              }
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications, toast, preferences]);

  const markAsRead = useCallback(async (notificationId: string) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    // Mark as read in database if it's a tag notification
    if (notificationId.startsWith('tag_')) {
      const dbId = notificationId.replace('tag_', '');
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', dbId);
    }
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  };
}
